import { useLocation } from 'react-router-dom'
import { useTodayAttendance } from './useTodayAttendance'

/**
 * redirect 사유. 'STATUS'=대기/휴원/퇴원(탭 잠금 UI가 별도 안내), 나머지=콘텐츠 사용료 게이트 토큰.
 * 소비측(App.tsx)이 사유별 toast 안내를 띄우는 데 사용.
 */
export type PreviewGateReason = 'STATUS' | 'NONE' | 'NOT_STARTED' | 'EXHAUSTED' | 'EXPIRED'

export type PreviewGateResult = { redirect: string; reason: PreviewGateReason } | null

export interface UsePreviewGateOptions {
  enabled?: boolean
}

/** 프리뷰(대기 원생) 홈 — 마이페이지(비번 변경). */
export const PREVIEW_HOME = '/my/profile'

/** 대기/휴원/퇴원 원생이 접근 못 하는 학습 라우트 prefix (축3b·축5C). 허용: /my/profile. */
const BLOCKED_PREFIXES = ['/my/today', '/my/library', '/my/playground', '/my/learn', '/playground', '/aiq-test']

/** 학습 라우트가 잠기는 파생 상태 — ENROLLED만 통과 (WITHDRAWN은 잔존 JWT 방어) */
const GATED_STATUSES = ['WAITING', 'ON_LEAVE', 'WITHDRAWN'] as const

/**
 * 프리뷰/소프트 게이트. 아래 두 조건 중 하나라도 해당하면 학습 라우트를 /my/profile로 redirect.
 *
 * 1. 대기(축3b)·휴원/퇴원(축5C) — enrollmentStatus in GATED_STATUSES
 * 2. 콘텐츠 사용료 미충족 — contentFeeGate non-null (NONE/NOT_STARTED/EXHAUSTED/EXPIRED)
 *
 * BE의 getTodayLesson 진입 가드(validateEntitlementPeriodAndQuota)와 동일 시맨틱(ContentFeeGate.evaluate).
 * fail-open (useDiagnosticFlow와 동일 정책).
 */
export function usePreviewGate(options: UsePreviewGateOptions = {}): PreviewGateResult {
  const { enabled = true } = options
  const path = useLocation().pathname
  const { data, isLoading, isError } = useTodayAttendance({ enabled })

  if (!enabled) return null
  if (isLoading || isError || !data) return null

  const statusGated = GATED_STATUSES.includes(data.enrollmentStatus as (typeof GATED_STATUSES)[number])
  const feeGated = !!data.contentFeeGate
  if (!statusGated && !feeGated) return null

  const blocked = BLOCKED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))
  if (!blocked) return null

  // 상태 게이트(대기/휴원/퇴원)가 우선 — 탭 잠금 UI가 안내를 담당하므로 'STATUS'.
  // 그 외(ENROLLED인데 막힘)는 사용료 게이트 토큰을 그대로 사유로 노출.
  const reason: PreviewGateReason = statusGated ? 'STATUS' : (data.contentFeeGate as PreviewGateReason)
  return { redirect: PREVIEW_HOME, reason }
}
